import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type FontWeightOption,
  type SignatureColumnType,
} from '@blaze-shared/api-contracts-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithAdvancedAlign,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type FontOptions,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type SignatureDetailsOption,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { generateSignatureFormField } from './generation/form-field';
import { signatureV2Migration } from './migrations/002-signature.migration';
import { signatureV3Migration } from './migrations/003-signature.migration';
import { signatureV4Migration } from './migrations/004-signature.migration';
import { signatureV5PaddingToMarginMigration } from './migrations/005-signature-padding-to-margin.migration';
import { SignatureComponent } from './signature.component';
import { SignaturePropsBuilder } from './signature.props-builder';

export type SignatureElement = CanvasElementFrom<'signature'>;
export type SignatureDef = WithProps<SignaturePropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    signature: SignatureDef;
  }
}

const DEFAULT_WIDTH = 260;
const DEFAULT_HEIGHT = 112;
const DEFAULT_HEIGHT_LABEL_TOP = 80;
const MIN_SIZE = 40;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = [];

registerElementDefinition({
  type: 'signature',
  componentType: SignatureComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Signature',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: SignaturePropsBuilder,
  generation: {
    formField: generateSignatureFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.input.readonly',
      placeholder: 'content.input.placeholder',
    },
  },
  version: 5,
  migrations: {
    2: signatureV2Migration,
    3: signatureV3Migration,
    4: signatureV4Migration,
    5: signatureV5PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Signature', dataType: 'Object' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        debounce: 500,
        schema: {
          id: '',
          name: 'signature',
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              isRequired: false,
              name: 'fullName',
            },
            {
              columnType: 'DateTime',
              dataType: 'DateTime',
              isProfilePicture: false,
              isRequired: false,
              name: 'signedDate',
            },
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              isRequired: false,
              name: 'userId',
            },
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              isRequired: false,
              name: 'signature',
            },
          ],
        },
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type SignaturePropsModel = PropsModel & {
  content: {
    title: ElementLabelPropsWithAdvancedAlign;
    input: {
      placeholder: string;
      readonly: boolean;
      signatureType: SignatureColumnType;
    };
    options: {
      detailsType: SignatureDetailsOption;
      /** deprecated in favor of `detailsType` */
      hideDate?: boolean;
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labelBlock: ElementLabelStyleProps;
    inputFont: FontOptions;
    input: {
      value: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
  };
};

function getDefaults(): PropsOf<'signature'> {
  return {
    content: {
      title: {
        label: 'Signature',
        position: 'left',
        align: { horizontal: 'left' },
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      input: {
        placeholder: 'Type your name here',
        readonly: false,
        signatureType: 'Typed',
      },
      options: {
        detailsType: 'full',
        hideDate: false,
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      inputFont: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        lineHeight: '$t.lineHeight',
        fontWeight: '$t.fontWeight' as FontWeightOption,
      },
      input: {
        value: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'signature'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('signature', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.input',
        'Input',
        [
          epc.fields.textInput('content.input.placeholder', 'Placeholder'),
          epc.fields.toggleField('content.input.readonly', 'Read Only'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Signature', dataType: 'Object' }],
            },
          ],
        },
      ),

      epc.subsections.subsection('content.options', 'Options', [
        epc.fields.radiosField(
          'content.options.detailsType',
          'Signature details',
          [
            { value: 'full', label: 'Name & Date' },
            { value: 'name', label: 'Name' },
            { value: 'date', label: 'Date' },
            { value: 'hidden', label: 'Hidden' },
          ],
          { renderType: 'classic' },
        ),
      ]),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),

      epc.subsections.visibilitySubsection(),

      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.inputFont', 'Text Input Font', [
          ...epc.fields.fontFamilyAndWeightFields(
            'style.inputFont.fontFamily',
            'style.inputFont.fontWeight',
          ),
          epc.fields.fontStyleField('style.inputFont.style', 'Style', ['bold']),
          epc.fields.sliderField('style.inputFont.fontSize', 'Size', [
            { min: 8, max: 72, unit: 'px' },
          ]),
        ]),

        epc.subsections.subsection('style.input', 'Text Input Style', [
          epc.fields.colorField('style.input.value', 'Value', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
            overrides: {
              horizontalAlign: epc.fields.alignField('content.title.align', 'Align', {
                defaultValue: { horizontal: 'left', vertical: 'middle' },
                excludedOptions: ['center'],
              }),
              position: epc.fields.inputTitlePositionField(
                'content.title.position',
                DEFAULT_HEIGHT,
                DEFAULT_HEIGHT_LABEL_TOP,
                'style.general.offset.margin',
              ),
            },
          },
        ),
      ],
      true,
    ),
  ]);
}
